import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LogOut, LayoutDashboard, Receipt, Calendar, MessagesSquare, ClipboardPlus, TriangleAlert, Home as HomeIcon, MoreHorizontal } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { BrandMark, BrandLockup } from '@/components/brand/BrandMark';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useIdleLogout } from '@/hooks/useIdleLogout';
import { PORTAL } from '@/constants/testIds';

const nav = [
  { to: '/portal', label: 'Overview', icon: LayoutDashboard, testid: PORTAL.navDashboard, end: true },
  { to: '/portal/invoices', label: 'Invoices', icon: Receipt, testid: PORTAL.navInvoices },
  { to: '/portal/schedule', label: 'Schedule', icon: Calendar, testid: PORTAL.navSchedule },
  { to: '/portal/messages', label: 'Messages', icon: MessagesSquare, testid: PORTAL.navMessages },
  { to: '/portal/service-requests', label: 'Service Requests', icon: ClipboardPlus, testid: PORTAL.navServiceRequests },
  { to: '/portal/reports', label: 'Damage Reports', icon: TriangleAlert, testid: PORTAL.navReports },
  { to: '/portal/properties', label: 'Properties', icon: HomeIcon, testid: PORTAL.navProperties },
];

// Only four fit a phone tab bar; the rest live behind "More" so nothing is
// unreachable on mobile.
const PRIMARY_TAB_COUNT = 4;

export function PortalShell({ children, title }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const { user, logout } = useAuth();
  const { warning, secondsLeft, stay } = useIdleLogout({
    enabled: !!user,
    onLogout: logout,
  });
  const initials = (user?.name || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-0">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:z-30 lg:w-[264px] lg:flex-col lg:border-r bg-card">
        <div className="flex h-14 items-center gap-2.5 border-b px-5">
          <BrandMark />
          <div>
            <div className="font-display font-semibold tracking-tight leading-tight">FlexiClean</div>
            <div className="text-[11px] text-muted-foreground leading-tight">Customer Portal</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {nav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? 'bg-primary/10 text-primary font-semibold' : 'text-foreground hover:bg-muted'}`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t p-3">
          <div className="flex items-center gap-2 mb-2 text-xs">
            <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">{initials}</div>
            <div className="min-w-0">
              <div className="truncate font-medium">{user?.name}</div>
              <div className="truncate text-[10px] text-muted-foreground">{user?.email}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full" onClick={logout} data-testid={PORTAL.navLogout}>
            <LogOut className="h-3 w-3 mr-2" />Log out
          </Button>
        </div>
      </aside>

      {/* Header (mobile) */}
      <header className="lg:hidden sticky top-0 z-40 h-14 border-b bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 flex items-center px-4 justify-between">
        <BrandLockup markClassName="h-7 w-7" />
        <Button variant="ghost" size="icon" onClick={logout} aria-label="Log out" data-testid={PORTAL.navLogout}><LogOut className="h-4 w-4" /></Button>
      </header>

      <div className="lg:pl-[264px]">
        <main className="px-4 md:px-6 lg:px-8 py-6 max-w-[1200px] mx-auto">
          {title && <div className="hidden lg:block text-sm text-muted-foreground mb-3">{title}</div>}
          {children}
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card/95 backdrop-blur border-t pb-[env(safe-area-inset-bottom)]">
        <div className="flex">
          {nav.slice(0, PRIMARY_TAB_COUNT).map(t => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testid}
              className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-1 min-h-[56px] py-2 text-[11px] font-medium transition-colors ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
              <t.icon className="h-5 w-5" />
              <span className="leading-none">{t.label.split(' ')[0]}</span>
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            data-testid="portal-tab-more"
            className="flex-1 flex flex-col items-center justify-center gap-1 min-h-[56px] py-2 text-[11px] font-medium text-muted-foreground transition-colors"
          >
            <MoreHorizontal className="h-5 w-5" />
            <span className="leading-none">More</span>
          </button>
        </div>
      </nav>

      {/* Everything past the fourth tab, plus log out */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="lg:hidden rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          <SheetHeader className="text-left mb-3"><SheetTitle>More</SheetTitle></SheetHeader>
          <div className="space-y-1">
            {nav.slice(PRIMARY_TAB_COUNT).map(t => (
              <NavLink
                key={t.to}
                to={t.to}
                onClick={() => setMoreOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-3 text-sm ${isActive ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-muted'}`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={logout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Idle warning modal */}
      <Dialog open={warning} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Are you still there?</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">
            For security, we’ll sign you out in <span className="font-semibold text-foreground">{secondsLeft}s</span> due to inactivity.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={logout}>Log out now</Button>
            <Button onClick={stay} data-testid={PORTAL.idleWarningStay}>Stay signed in</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
